// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import type { StatusToken } from '../../../../utils/entityHelpers';
import { StatusMark } from './StatusMark';

/** What is wrong behind a fold: the worst status and how many members carry it. */
export interface DownstreamStatus {
  /** Worst status among the folded members. */
  status: StatusToken;
  /** Members in fault. */
  faultCount: number;
  /** Members degraded. */
  warnCount: number;
  /** Hover text, e.g. `14 IT Racks folded, 1 alarming`. */
  label: string;
}

/**
 * A second status mark for what a box or bar stands over but does not draw.
 *
 * Folding racks into `14 IT Racks` is what makes a real site fit on screen, and
 * it is also the one move on this drawing that can hide a fault: the RPP's own
 * status says nothing about its racks. So a fold with an unhealthy member gets
 * this mark beside the asset's own — the same `StatusMark` shape the rest of
 * the drawing uses, so a reader who knows the key already knows what it means.
 * Healthy and no-data folds draw nothing; the mark is a warning, not a badge.
 *
 * Decorative, like `StatusMark`: the counts are spoken in the accessible name
 * the stage builds, and the hover title restates them for a mouse.
 */
export const DownstreamMark = ({
  downstream,
  className,
}: {
  downstream?: DownstreamStatus;
  className: string;
}) => {
  if (!downstream || (downstream.status !== 'fault' && downstream.status !== 'warn')) return null;
  return (
    <span
      className={`${className} sld-downstream sld-downstream--${downstream.status}`}
      data-sld-downstream={downstream.status}
      title={downstream.label}
    >
      <StatusMark status={downstream.status} />
    </span>
  );
};
